import type { MiniAppManifest } from '../types';

interface AppPreviewProps {
  previewType?: MiniAppManifest['previewType'];
  appName: string;
}

/** Fake macOS window chrome bar with colored dots and centered app name. */
function WindowChrome({ appName }: { appName: string }) {
  return (
    <div className="flex h-8 items-center gap-1.5 border-b border-border-secondary bg-nightshade-100 px-2.5">
      <div className="h-2 w-2 rounded-full" style={{ background: '#ff5f57' }} />
      <div className="h-2 w-2 rounded-full" style={{ background: '#ffbd2e' }} />
      <div className="h-2 w-2 rounded-full" style={{ background: '#28c840' }} />
      <span className="flex-1 text-center text-[10px] text-content-tertiary">
        {appName}
      </span>
    </div>
  );
}

// ─── Calendar Preview ───────────────────────────────────────────────

function CalendarPreview() {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

  // 14 cells — 2 rows of 7. Some have colored event dots.
  type CellDef = { dot?: string; highlight?: boolean };
  const cells: CellDef[] = [
    {},
    {},
    { dot: 'bg-tangerine-500' },
    { dot: 'bg-matcha-500' },
    { highlight: true },
    {},
    {},
    {},
    { dot: 'bg-pomegranate-500' },
    {},
    {},
    { dot: 'bg-tangerine-500' },
    {},
    {},
  ];

  return (
    <div className="flex flex-1 flex-col gap-2.5 p-4">
      {/* Header row: title + month/list toggle */}
      <div className="flex items-center justify-between">
        <div className="h-3.5 w-[120px] rounded bg-nightshade-300" />
        <div className="flex gap-1.5">
          <div className="flex h-[22px] w-[50px] items-center justify-center rounded-md bg-nightshade-200 text-[9px] text-content-tertiary">
            Month
          </div>
          <div className="flex h-[22px] w-[40px] items-center justify-center rounded-md border border-border-secondary bg-bg-primary text-[9px] text-content-tertiary">
            List
          </div>
        </div>
      </div>

      {/* Day headers */}
      <div className="mt-1 grid grid-cols-7 gap-[3px]">
        {days.map((d) => (
          <div
            key={d}
            className="text-center text-[8px] text-content-placeholder"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Calendar cells */}
      <div className="grid grid-cols-7 gap-[3px]">
        {cells.map((cell, i) => (
          <div
            key={i}
            className={`flex aspect-square items-center justify-center rounded ${
              cell.highlight
                ? 'border border-blueberry-200 bg-blueberry-50'
                : 'border border-border-secondary bg-bg-primary'
            }`}
          >
            {cell.dot && (
              <div className={`h-1 w-1 rounded-full ${cell.dot}`} />
            )}
          </div>
        ))}
      </div>

      {/* Mock card row */}
      <div className="h-10 rounded-lg border border-border-secondary bg-bg-primary" />
    </div>
  );
}

// ─── Waterfall Preview ──────────────────────────────────────────────

function WaterfallPreview() {
  const providers: {
    name: string;
    width: string;
    barColor: string;
    labelColor: string;
    label: string;
  }[] = [
    {
      name: 'Clearbit',
      width: '80%',
      barColor: 'bg-matcha-400',
      labelColor: 'text-matcha-600',
      label: '200ms',
    },
    {
      name: 'ZoomInfo',
      width: '60%',
      barColor: 'bg-blueberry-400',
      labelColor: 'text-blueberry-600',
      label: '150ms',
    },
    {
      name: 'Apollo',
      width: '30%',
      barColor: 'bg-pomegranate-500',
      labelColor: 'text-pomegranate-500',
      label: 'timeout',
    },
    {
      name: 'PDL',
      width: '90%',
      barColor: 'bg-matcha-400',
      labelColor: 'text-matcha-600',
      label: '280ms',
    },
  ];

  return (
    <div className="flex flex-1 flex-col gap-2.5 p-4">
      <div className="flex flex-col gap-1.5">
        {providers.map((p) => (
          <div key={p.name} className="flex items-center gap-2">
            <div className="w-[50px] text-right text-[9px] text-content-tertiary">
              {p.name}
            </div>
            <div className="relative h-3.5 flex-1 rounded-[3px] bg-nightshade-100">
              <div
                className={`absolute inset-y-0 left-0 rounded-[3px] ${p.barColor}`}
                style={{ width: p.width }}
              />
            </div>
            <span className={`text-[9px] ${p.labelColor}`}>{p.label}</span>
          </div>
        ))}
      </div>
      <div className="h-10 rounded-lg border border-border-secondary bg-bg-primary" />
      <div className="h-10 rounded-lg border border-border-secondary bg-bg-primary" />
    </div>
  );
}

// ─── Chart Preview ──────────────────────────────────────────────────

function ChartPreview() {
  const bars = [40, 55, 45, 70, 90, 65, 30];

  return (
    <div className="flex flex-1 flex-col gap-2.5 p-4">
      <div className="flex h-20 items-end gap-1.5 pt-2">
        {bars.map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-[3px] border border-b-0 border-matcha-200 bg-matcha-100"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <div className="h-10 rounded-lg border border-border-secondary bg-bg-primary" />
      <div className="h-10 rounded-lg border border-border-secondary bg-bg-primary" />
    </div>
  );
}

// ─── Kanban Preview ─────────────────────────────────────────────────

function KanbanPreview() {
  const columns: { title: string; cards: number; won?: boolean }[] = [
    { title: 'Prospecting', cards: 2 },
    { title: 'Qualified', cards: 1 },
    { title: 'Proposal', cards: 3 },
    { title: 'Won', cards: 1, won: true },
  ];

  return (
    <div className="flex flex-1 gap-2 p-4">
      {columns.map((col) => (
        <div key={col.title} className="flex flex-1 flex-col gap-1">
          <div
            className={`text-center text-[8px] font-semibold ${
              col.won
                ? 'text-matcha-600'
                : 'text-content-placeholder'
            }`}
          >
            {col.title}
          </div>
          {Array.from({ length: col.cards }).map((_, i) => (
            <div
              key={i}
              className={`h-[30px] rounded-md border ${
                col.won
                  ? 'border-matcha-200 bg-matcha-50'
                  : 'border-border-secondary bg-bg-primary'
              }`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Flow Preview ───────────────────────────────────────────────────

function FlowPreview() {
  const nodes: { label: string; bgClass: string; borderClass: string; textClass: string }[] = [
    {
      label: 'Email #1',
      bgClass: 'bg-ube-50',
      borderClass: 'border-ube-200',
      textClass: 'text-ube-700',
    },
    {
      label: 'LinkedIn',
      bgClass: 'bg-blueberry-50',
      borderClass: 'border-blueberry-200',
      textClass: 'text-blueberry-700',
    },
    {
      label: 'Email #2',
      bgClass: 'bg-tangerine-50',
      borderClass: 'border-tangerine-200',
      textClass: 'text-tangerine-700',
    },
  ];

  const waits = ['Wait 3 days', 'Wait 2 days'];

  return (
    <div className="flex flex-1 flex-col items-center gap-1 p-4">
      {nodes.map((node, i) => (
        <div key={node.label} className="flex flex-col items-center gap-1">
          {i > 0 && (
            <>
              <div className="h-3 w-px bg-nightshade-300" />
              <div className="rounded bg-nightshade-100 px-2 py-0.5 text-[8px] text-content-tertiary">
                {waits[i - 1]}
              </div>
              <div className="h-3 w-px bg-nightshade-300" />
            </>
          )}
          <div
            className={`flex h-7 w-[120px] items-center justify-center rounded-md border text-[9px] ${node.bgClass} ${node.borderClass} ${node.textClass}`}
          >
            {node.label}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Config Preview ─────────────────────────────────────────────────

function ConfigPreview() {
  const rows: (
    | { key: string; type: 'toggle'; on: boolean }
    | { key: string; type: 'value'; value: string }
  )[] = [
    { key: 'enable_v4_pipeline', type: 'toggle', on: true },
    { key: 'max_enrichment_retries', type: 'value', value: '3' },
    { key: 'beta_sequence_builder', type: 'toggle', on: false },
    { key: 'daily_api_limit', type: 'value', value: '10000' },
  ];

  return (
    <div className="flex flex-1 flex-col gap-2.5 p-4">
      {rows.map((row, i) => (
        <div
          key={row.key}
          className={`flex items-center justify-between py-1 ${
            i < rows.length - 1 ? 'border-b border-border-secondary' : ''
          }`}
        >
          <span className="text-[9px] text-content-secondary">{row.key}</span>
          {row.type === 'toggle' ? (
            <div
              className={`relative h-4 w-7 rounded-full ${
                row.on ? 'bg-matcha-500' : 'bg-nightshade-300'
              }`}
            >
              <div
                className={`absolute top-0.5 h-3 w-3 rounded-full bg-white ${
                  row.on ? 'right-0.5' : 'left-0.5'
                }`}
              />
            </div>
          ) : (
            <span className="rounded bg-blueberry-50 px-1.5 py-0.5 text-[9px] text-blueberry-600">
              {row.value}
            </span>
          )}
        </div>
      ))}
      <div className="mt-2 h-10 rounded-lg border border-border-secondary bg-bg-primary" />
    </div>
  );
}

// ─── Default Fallback ───────────────────────────────────────────────

function DefaultPreview({ appName }: { appName: string }) {
  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <span className="text-xs text-content-placeholder">{appName}</span>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────

const previewRenderers: Record<
  string,
  React.ComponentType<{ appName: string }>
> = {
  calendar: CalendarPreview,
  waterfall: WaterfallPreview,
  chart: ChartPreview,
  kanban: KanbanPreview,
  flow: FlowPreview,
  config: ConfigPreview,
};

export function AppPreview({ previewType, appName }: AppPreviewProps) {
  const Renderer = previewType
    ? previewRenderers[previewType] ?? DefaultPreview
    : DefaultPreview;

  return (
    <div className="overflow-hidden rounded-xl border border-border-secondary bg-bg-secondary">
      <div className="flex flex-col">
        <WindowChrome appName={appName} />
        <Renderer appName={appName} />
      </div>
    </div>
  );
}
